import { Component, OnInit } from '@angular/core';
import { AuthClient } from 'src/app/api/auth-api.generated';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CurrencySettingDto, UserCurrencySettingsDto, UsersClient } from 'src/app/api/wallet-api.generated';
import { AppSettingsService } from 'src/app/shared/services/app-settings/app-settings.service';
import { ApplicationFeatures, MaxFractionDigits } from 'src/app/shared/enums/enums';
import { IDropDownItem } from 'src/app/shared/models/drop-down-item';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { ToastrMessageServiceService } from 'src/app/shared/services/toastr-message/toastr-message-service.service';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { ConfigurationService } from '../../shared/services/configuration/configuration.service';
import {Language} from '../../shared/interfaces/language';

const maxFractionDigitsKeyPrefix = 'max-fraction-digits.';

@Component({
  selector: 'app-general-settings',
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss'],
})
export class GeneralSettingsComponent implements OnInit {

  public currentTab = 'settings';
  public isLoading = false;

  languages: Language[];
  currentLanguage: string;
  isSingleLang;

  public isEthDisabled: boolean = this.configurationService.checkForDisabledFeature(ApplicationFeatures.ethIntegration);

  maxFractionDigits: string[];
  userCurrencySettingsDto: UserCurrencySettingsDto;
  updateMaxFractionDigitsSuccessMsg = _('settings.general.update-max-fraction-digits-success-message');
  updateMaxFractionDigitsFailMsg = _('settings.general.update-max-fraction-digits-fail-message');

  public selectedCurrency;
  public selectedMaxFractionDigits: number;

  public currencies: CurrencySettingDto[];
  public fractionDigits: IDropDownItem[];
  private currencySettingId: string;

  constructor(
    private langService: LangService,
    private transalateService: TranslateService,
    private toastrMessageServiceService: ToastrMessageServiceService,
    private authClient: AuthClient,
    private configurationService: ConfigurationService,
    private readonly usersClient: UsersClient
  ) {
    langService.init();
    this.languages = this.langService.supportedLanguages;
    this.currentLanguage = this.langService.language;
    this.isSingleLang = this.langService.isSingleLang;

    // this.token = this.authService.getToken();

    const maxFractionDigitsKeys = Object.keys(MaxFractionDigits);

    this.maxFractionDigits = maxFractionDigitsKeys
      .slice(maxFractionDigitsKeys.length / 2)
      .map((x) => `${maxFractionDigitsKeyPrefix}${x}`);
  }

  ngOnInit() {
    // this.authService.refreshSession().subscribe((result) => {
    //   this.token = result.accessToken;
    // });

    this.usersClient.getCurrencySettings()
      ?.pipe(take(1))
      .subscribe((result: UserCurrencySettingsDto) => this.initCurrencySettings(result, null));

    this.transalateService
      .get(this.maxFractionDigits)
      ?.pipe(map((x) => this.mapDropDownList(x))).subscribe((result) => {
        this.fractionDigits = result;
      });

  }

  mapDropDownList(res: any): IDropDownItem[] {
    const dropDownItemList: IDropDownItem[] = [];
    for (const key in res) {
      if (Object.prototype.hasOwnProperty.call(res, key)) {
        const element = res[key];
        const currencyItem: IDropDownItem = {
          name: element,
          id: MaxFractionDigits[key.split('.')[1]],
        };
        dropDownItemList.push(currencyItem);
      }
    }

    return dropDownItemList;
  }

  async onLanguageChange(lang): Promise<void> {
    this.langService.language = lang.code;
    this.currentLanguage = this.langService.language;
    await this.authClient.changeLanguage(lang.shorthand).toPromise();
  }

  onChangePassword(e: { stopPropagation: () => void; preventDefault: () => void }): void {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    const changePasswordUrl =
      AppSettingsService.authServiceUrl +
      '/Account/ChangePassword?returnUrl=' +
      encodeURIComponent(window.location.href);

    setTimeout(() => {
      document.location.href = changePasswordUrl;
    }, 0);
    // setTimeout -> workaround so that redirect is not blocked by some browsers
  }

  onCurrencyChange(currency: CurrencySettingDto) {
    this.selectedMaxFractionDigits = currency.maxFractionDigits;
  }

  onMaxFractionDigitChange(value: IDropDownItem): void {
    this.isLoading = true;

    const currencySettingDto: UserCurrencySettingsDto =  {
      id: this.currencySettingId,
      currencySettings: this.currencies.map(currency => currency.currency === this.selectedCurrency.currency ?
        { ...currency, maxFractionDigits: Number(value.id)} : currency )
    };

    this.usersClient.updateCurrencySettings(currencySettingDto).pipe(take(1),
      catchError(() => {
        this.toastrMessageServiceService.error(this.updateMaxFractionDigitsFailMsg);
        return of(null);
      })).subscribe(() => {
        const selectedCurrencyIndex = currencySettingDto.currencySettings
          .findIndex((currencySetting) => currencySetting.currency === this.selectedCurrency.currency);

        this.initCurrencySettings(currencySettingDto, selectedCurrencyIndex);
        this.toastrMessageServiceService.success(this.updateMaxFractionDigitsSuccessMsg);
        this.isLoading = false;
      });

  }

  private initCurrencySettings(result: UserCurrencySettingsDto, selectedCurrencyIndex): void {
    if (result) {
      this.currencySettingId = result.id;
      this.currencies = result.currencySettings;
      this.selectedCurrency = selectedCurrencyIndex ? this.currencies[selectedCurrencyIndex] : this.currencies[0];
      this.selectedMaxFractionDigits = this.selectedCurrency.maxFractionDigits;
    }
  }
}
